import { useEffect, useState } from "react";
import clsx from "clsx";
import { Select as SelectProps } from "./index.types";
import "./index.css";
import Button from "../button";
import { TfiAngleDown } from "react-icons/tfi";
import Input from "../input";

export function Select({
  inputClassName = "",
  classNameGroup = "",
  options,
  styleGroup,
  defaultValue,
  setFormSelected,
  clearError,
  resetForm,
  onFormBlur,
  handleSelect,
  label,
  labelClassName,
  error,
  backgroundColor,
  isRequired,
  ...rest
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(defaultValue ?? options?.[0] ?? "");
  // Separate from `value`: only sets while the user is actively typing to
  // filter the list. Keeping this apart from `value` matters because `value`
  // also holds the selected/default option, which must not itself act as a
  // filter narrowing the dropdown down to just that one entry.
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const { name } = rest;

  // If options arrive asynchronously after mount and nothing has been typed
  // or selected yet, adopt the newly-available default instead of staying
  // permanently empty. Only fires while value is still unset, so it never
  // clobbers a value the user has already chosen or typed.
  useEffect(() => {
    if (!value && options && options.length > 0) {
      setValue(defaultValue ?? options[0]);
    }
  }, [options]);

  const filteredOptions = searchTerm
    ? options?.filter((option) => option.includes(searchTerm))
    : options;

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInputClose = () => {
    if (onFormBlur) {
      onFormBlur();
    }
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleValueChange = (e: { target: { value: string } }) => {
    setValue(e.target.value);
    setSearchTerm(e.target.value);
  };

  const handleSelectChange = (e: { target: { name: string } }) => {
    const { name: value } = e.target;

    setSearchTerm(undefined);

    if (setFormSelected) {
      setFormSelected(name, value);
      if (clearError) {
        clearError(name);
      }

      if (resetForm) {
        resetForm();
      }
    } else {
      setValue(value);
    }

    if (handleSelect) {
      handleSelect(value);
    }

    handleClose();
  };

  return (
    <div
      className={clsx("select-group", classNameGroup)}
      style={{ backgroundColor: backgroundColor, ...styleGroup }}
    >
      <Input
        inputClassName={clsx(
          "select",
          {
            "select--focus": isOpen,
          },
          inputClassName
        )}
        value={value}
        error={error}
        required={isRequired}
        placeholder="Select a value"
        icon={{
          className: "select__arrow",
          element: (
            <Button
              type="button"
              size="large"
              mode="icon"
              className={clsx("select__arrow-button", {
                "select__arrow-button--open": isOpen,
              })}
              onClick={handleToggle}
              onBlur={handleClose}
            >
              <TfiAngleDown size={24} />
            </Button>
          ),
        }}
        onFocus={handleOpen}
        onBlur={handleInputClose}
        onChange={handleValueChange}
        autoComplete="off"
        label={label}
        labelClassName={labelClassName}
        {...rest}
      />
      {isOpen && (
        <div className="select__box">
          {(filteredOptions ?? []).map((option, index) => (
            <Button
              key={`index-${index}`}
              type="button"
              mode="secondary"
              className="select__option"
              name={option}
              title={option}
              onMouseDown={handleSelectChange as any}
            >
              {option}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Select;
